import { useEffect, useState } from 'react';
import axios from 'axios';
import { contractorToken } from './contractorAuth';
import { storedViewAsContractorId, storedViewAsUserId } from './viewAs';

/**
 * AI in tickets and docs (wave 3, batch 5): what this person may use, and
 * the calls.
 *
 * GET /ai/status answers { ready, configured, on, it, can: { suggest,
 * summarise, draft, write } } - the server knows who is IT staff and
 * whether it has a key, which the browser cannot. Asked once and shared,
 * like lib/features.js, and asked again when who is asking changes (View
 * as). Until it answers every button is HIDDEN (null): unlike the menus,
 * a Claude button that appears and then vanishes would be worse than one
 * that appears a moment late. A failed ask hides them too - the server
 * refuses whatever it refuses either way.
 *
 * Claude only suggests and drafts. Nothing here sets a field, sends a
 * message or publishes a page; the screens put the words where a person can
 * change them, and the person decides.
 */
const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

const NONE = Object.freeze({ ready: false, configured: false, on: false, it: false, can: { suggest: false, summarise: false, draft: false, write: false } });

let cached = null;          // { value, for }
let inflight = null;
const listeners = new Set();

const askingAs = () => {
  const token = contractorToken();
  if (token) return `t:${token.slice(-12)}`;
  return `s:${storedViewAsUserId() || ''}:${storedViewAsContractorId() || ''}`;
};

export function fetchAiStatus({ force = false } = {}) {
  const who = askingAs();
  // A contractor never has AI in tickets: not even asked.
  if (contractorToken() || storedViewAsContractorId()) return Promise.resolve(NONE);
  if (!force && cached && cached.for === who) return Promise.resolve(cached.value);
  if (!force && inflight) return inflight;
  inflight = axios.get(`${API_BASE}/ai/status`)
    .then((r) => ({ ...NONE, ...(r.data || {}), can: { ...NONE.can, ...(r.data?.can || {}) } }))
    .catch(() => NONE)
    .then((value) => {
      cached = { value, for: who };
      inflight = null;
      listeners.forEach((fn) => { try { fn(value); } catch { /* one listener must not break the rest */ } });
      return value;
    });
  return inflight;
}

/** What I may use: null until known. */
export function useAiStatus() {
  const [value, setValue] = useState(cached && cached.for === askingAs() ? cached.value : null);
  useEffect(() => {
    let live = true;
    const on = (v) => { if (live) setValue(v); };
    listeners.add(on);
    fetchAiStatus().then(on);
    return () => { live = false; listeners.delete(on); };
  }, []);
  return value;
}

/** The server's sentence for a failed AI call - a limit, a refusal, a timeout - or ours. */
export function aiError(err, fallback = 'Claude could not help just now.') {
  const r = err?.response;
  if (!r) return 'Claude could not be reached. Check your connection and try again.';
  return r.data?.error || fallback;
}

export const aiApi = {
  suggest: (title, description) => axios.post(`${API_BASE}/ai/tickets/suggest`, { title, description }).then((r) => r.data),
  summary: (id) => axios.get(`${API_BASE}/ai/tickets/${id}/summary`).then((r) => r.data),
  summarise: (id) => axios.post(`${API_BASE}/ai/tickets/${id}/summary`, {}).then((r) => r.data),
  reply: (id) => axios.post(`${API_BASE}/ai/tickets/${id}/reply`, {}).then((r) => r.data),
  write: (body) => axios.post(`${API_BASE}/ai/docs/write`, body).then((r) => r.data),
  usage: (month) => axios.get(`${API_BASE}/ai/usage`, { params: month ? { month } : {} }).then((r) => r.data)
};

/**
 * Has the text changed enough to ask again? Words in common over words in
 * either (Jaccard) under 0.6 - a sentence added, not a typo fixed.
 */
export function changedMuch(a, b) {
  const words = (s) => new Set(String(s || '').toLowerCase().match(/[\p{L}\p{N}]+/gu) || []);
  const x = words(a);
  const y = words(b);
  if (!x.size && !y.size) return false;
  let both = 0;
  x.forEach((w) => { if (y.has(w)) both += 1; });
  return both / (x.size + y.size - both) < 0.6;
}

/**
 * The model's Markdown (a summary: ## headings, - lists, paragraphs) as
 * React-free blocks for a small renderer: [{ kind: 'h'|'li'|'p', text }].
 */
export function blocksOf(md) {
  return String(md || '').split(/\r?\n/).map((l) => l.trim()).filter(Boolean).map((l) => {
    if (/^#{1,6}\s+/.test(l)) return { kind: 'h', text: l.replace(/^#{1,6}\s+/, '') };
    if (/^([-*]|\d+[.)])\s+/.test(l)) return { kind: 'li', text: l.replace(/^([-*]|\d+[.)])\s+/, '') };
    return { kind: 'p', text: l };
  });
}
